import type { LucideIcon } from 'lucide-react';
import { ChevronsUpDown } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';

/**
 * A filter that takes several values at once.
 *
 * An empty selection means **everything**, not nothing — an empty multi-select
 * that filtered to no rows would be a filter bar that starts by hiding the
 * data. Every caller here wants the opposite, so "none chosen" is the reset.
 *
 * Sized and unlabelled to sit inside a `FilterBar` custom field, which supplies
 * the label above it.
 */
export interface MultiSelectOption {
  value: string;
  label: string;
}

export function MultiSelect({
  id,
  options,
  selected,
  onChange,
  disabled,
  icon: Icon,
  heading,
  allLabel,
  summarise,
  matchTriggerWidth,
}: {
  id?: string;
  options: MultiSelectOption[];
  selected: string[];
  onChange: (values: string[]) => void;
  disabled?: boolean;
  icon?: LucideIcon;
  /** Small heading above the list. */
  heading: string;
  /** Shown when nothing is chosen, and as the reset row. */
  allLabel: string;
  /** How to name a selection of more than one. */
  summarise: (count: number) => string;
  /**
   * Size the menu to the trigger instead of the fixed filter-bar width. For a
   * full-width field in a form, where a narrow menu hanging off the left edge
   * looks detached from the control that opened it.
   */
  matchTriggerWidth?: boolean;
}) {
  const label =
    selected.length === 0
      ? allLabel
      : selected.length === 1
        ? (options.find((option) => option.value === selected[0])?.label ??
          summarise(1))
        : summarise(selected.length);

  const toggle = (value: string) =>
    onChange(
      selected.includes(value)
        ? selected.filter((entry) => entry !== value)
        : [...selected, value]
    );

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          id={id}
          type="button"
          disabled={disabled}
          aria-label={heading}
          className={cn(
            'border-input bg-background flex h-9 w-full items-center justify-between gap-2 rounded-md border px-3 text-sm',
            'focus-visible:ring-ring/50 outline-none focus-visible:ring-[3px]',
            disabled && 'pointer-events-none opacity-50'
          )}
        >
          <span className="flex min-w-0 items-center gap-2">
            {Icon && <Icon className="text-muted-foreground size-4 shrink-0" />}
            <span className="truncate">{label}</span>
          </span>
          <ChevronsUpDown className="text-muted-foreground size-4 shrink-0" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        className={cn(
          'max-h-72 overflow-y-auto',
          matchTriggerWidth
            ? 'w-[var(--radix-dropdown-menu-trigger-width)]'
            : 'w-64'
        )}
      >
        <DropdownMenuLabel>{heading}</DropdownMenuLabel>
        <DropdownMenuItem onSelect={() => onChange([])}>
          {allLabel}
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        {options.map((option) => (
          <DropdownMenuCheckboxItem
            key={option.value}
            checked={selected.includes(option.value)}
            onCheckedChange={() => toggle(option.value)}
            // Without this the menu closes after each tick, which makes
            // choosing three things three trips.
            onSelect={(event) => event.preventDefault()}
          >
            {option.label}
          </DropdownMenuCheckboxItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
